// Supabase: Postgres through PostgREST, Realtime for live changes, Auth for magic-link sign-in.
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Job, Pm, Profile, Stage, Task } from '@shared/index';
import { ReadOnlyError, type Backend, type ChangeHandler, type SyncRun, type Table, type UserRow } from './types';

const PAGE = 1000;

export function supabaseBackend(url: string, anonKey: string, syncUrl: string): Backend {
  const sb: SupabaseClient = createClient(url, anonKey, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, flowType: 'pkce' },
    realtime: { params: { eventsPerSecond: 20 } },
  });

  /** Read a whole table, a page at a time, so there's no row cap. */
  async function all<T>(table: Table, order: string): Promise<T[]> {
    const out: T[] = [];
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await sb.from(table).select('*').order(order).range(from, from + PAGE - 1);
      if (error) throw error;
      out.push(...(data as T[]));
      if (!data || data.length < PAGE) return out;
    }
  }

  /** An update that RLS hides comes back with no row: that means the user can't edit. */
  async function one<T>(q: PromiseLike<{ data: unknown; error: { code?: string; message: string } | null }>): Promise<T> {
    const { data, error } = await q;
    if (error) {
      if (error.code === '42501' || error.code === 'PGRST116' || /permission denied|row-level security/i.test(error.message)) throw new ReadOnlyError();
      throw error;
    }
    if (!data) throw new ReadOnlyError();
    return data as T;
  }

  return {
    kind: 'supabase',

    async session() {
      const { data } = await sb.auth.getSession();
      return data.session?.user.email ?? null;
    },
    onSignedIn(cb) {
      sb.auth.onAuthStateChange((ev) => { if (ev === 'SIGNED_IN') cb(); });
    },
    async sendMagicLink(email) {
      const { error } = await sb.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: location.origin + location.pathname, shouldCreateUser: true },
      });
      if (error) throw error;
    },
    async verifyLink(tokenHash, type) {
      const { error } = await sb.auth.verifyOtp({ token_hash: tokenHash, type: type as 'email' | 'signup' | 'magiclink' });
      if (error) throw error;
    },
    async verifyCode(email, code) {
      const { error } = await sb.auth.verifyOtp({ email, token: code, type: 'email' });
      if (error) throw error;
    },
    async signOut() {
      await sb.auth.signOut();
    },
    async profile() {
      const { data: u } = await sb.auth.getUser();
      if (!u.user) return null;
      const { data, error } = await sb.from('profiles').select('id, email, role, pm_name').eq('id', u.user.id).maybeSingle();
      if (error) throw error;
      return data as Profile | null;
    },

    async loadAll() {
      const [jobs, stages, tasks, pms] = await Promise.all([
        all<Job>('jobs', 'quote'), all<Stage>('stages', 'id'), all<Task>('tasks', 'id'), all<Pm>('pms', 'sort_order'),
      ]);
      jobs.forEach((j) => { j.quoted = Number(j.quoted); j.invoiced = Number(j.invoiced); j.remaining = Number(j.remaining); });
      return { jobs, stages, tasks, pms };
    },

    subscribe(onChange: ChangeHandler, onReconnect) {
      let wasUp = false;
      const ch = sb.channel('tracker');
      for (const t of ['jobs', 'stages', 'tasks', 'pms'] as Table[]) {
        ch.on('postgres_changes', { event: '*', schema: 'public', table: t }, (p) => {
          onChange(t, p.eventType as 'INSERT' | 'UPDATE' | 'DELETE',
            Object.keys(p.new || {}).length ? (p.new as Record<string, unknown>) : null,
            Object.keys(p.old || {}).length ? (p.old as Record<string, unknown>) : null);
        });
      }
      ch.subscribe((status) => {
        if (status === 'SUBSCRIBED') { if (wasUp) onReconnect(); wasUp = true; }
      });
      // Realtime needs the user's token to apply the read policies; keep it current.
      sb.auth.onAuthStateChange((_ev, s) => { if (s) sb.realtime.setAuth(s.access_token); });
    },

    updateJob: (quote, patch) => one<Job>(sb.from('jobs').update(patch).eq('quote', quote).select().maybeSingle()),
    insertStage: (row) => one<Stage>(sb.from('stages').insert(row).select().single()),
    updateStage: (id, patch) => one<Stage>(sb.from('stages').update(patch).eq('id', id).select().maybeSingle()),
    async deleteStage(id) {
      const { error } = await sb.from('stages').delete().eq('id', id);
      if (error) throw error;
    },
    insertTask: (row) => one<Task>(sb.from('tasks').insert(row).select().single()),
    updateTask: (id, patch) => one<Task>(sb.from('tasks').update(patch).eq('id', id).select().maybeSingle()),
    async deleteTask(id) {
      const { error } = await sb.from('tasks').delete().eq('id', id);
      if (error) throw error;
    },

    async listUsers() {
      const { data, error } = await sb.from('profiles').select('id, email, role, pm_name, created_at').order('email');
      if (error) throw error;
      return data as UserRow[];
    },
    async updateUser(id, patch) {
      await one(sb.from('profiles').update(patch).eq('id', id).select().maybeSingle());
    },
    async listSyncRuns(limit) {
      const { data, error } = await sb.from('sync_runs').select('*').order('started_at', { ascending: false }).limit(limit);
      if (error) throw error;
      return data as SyncRun[];
    },
    async syncNow() {
      if (!syncUrl) return { ok: false, message: 'The sync address (VITE_SYNC_URL) isn’t set.' };
      const { data } = await sb.auth.getSession();
      const res = await fetch(syncUrl.replace(/\/$/, '') + '/sync', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + (data.session?.access_token ?? '') },
      });
      const body = await res.json().catch(() => ({} as Record<string, unknown>));
      return { ok: res.ok, message: String(body.message ?? (res.ok ? 'Sync finished' : 'Sync failed (' + res.status + ')')) };
    },
  };
}
